import { useQuery } from '@tanstack/react-query'
import { api, type SeriesBet, type SeriesView } from '../api'
import { formatTime, relativeTime } from '../time'
import { marketLabel } from '../markets'
import { Detail } from './Detail'
import { Term } from './Term'
import { TeamBadge } from './TeamBadge'

/**
 * A series — the unit people follow — with each map's result underneath.
 *
 * Everything is expressed from team A's point of view, fixed by the first game. That matters
 * because <b>sides swap between maps</b>: a team on radiant in game one is usually dire in game two,
 * so a column of "radiant %" down a series would flip meaning halfway and read as nonsense.
 *
 * The forecast band changes with the state of the series, because the interesting question does:
 *
 * - **Still open** — what are the chances of taking it from here, given the current score. A live
 *   forecast, worth a bar.
 * - **Decided** — what we said *before* it started, and whether that was right. A finished series
 *   has no probability left; showing one collapsed to 100% with a progress bar implies uncertainty
 *   that no longer exists and tells the reader nothing they cannot see from the scoreline.
 */
export function SeriesCard({
  series,
  showLeague = false,
}: {
  series: SeriesView
  showLeague?: boolean
}) {
  const aLeads = series.scoreA > series.scoreB
  const bLeads = series.scoreB > series.scoreA
  const started = series.maps.some((map) => map.teamAWon !== null || map.isLive)

  return (
    <article
      // min-w-0 is load-bearing, not tidying. This card is a grid item, and a grid item's default
      // `min-width: auto` means it refuses to shrink below its content's minimum — so one wide row
      // inside pushed the card to 387px inside a 309px column on a phone, and the whole page
      // scrolled sideways. Overflow always surfaces at the outermost element that will not shrink,
      // which is why the fix belongs here rather than where the wide content is.
      className={`min-w-0 rounded-lg border bg-surface transition-colors ${
        series.isLive ? 'border-live-edge' : 'border-line hover:border-accent-edge'
      }`}
    >
      <header className="flex items-start justify-between gap-3 border-b border-line p-4 pb-3">
        <div className="min-w-0 flex-1">
          {/*
            Two shapes for the same scoreline, because the inline one does not survive a phone.
            "A 1 – 1 B" wrapped onto two lines reads as "A is 1–1" with B underneath — the score
            appears to belong to whichever name shares its line. On a site people check before
            placing a bet, a scoreline that can be read as the wrong thing is not a cosmetic issue.

            Below sm it becomes an ordinary scoreboard: one team per row, score aligned right, which
            cannot be misparsed however long the names are.
          */}
          <div className="flex flex-col gap-1 sm:hidden">
            <TeamRow
              name={series.teamA}
              logo={series.teamALogo}
              score={series.scoreA}
              leading={aLeads}
            />
            <TeamRow
              name={series.teamB}
              logo={series.teamBLogo}
              score={series.scoreB}
              leading={bLeads}
            />
          </div>

          <div className="hidden flex-wrap items-baseline gap-x-2 gap-y-1 sm:flex">
            <span
              className={`flex items-center gap-1.5 text-sm ${aLeads ? 'font-semibold text-ink' : 'text-ink-soft'}`}
            >
              <TeamBadge name={series.teamA} logo={series.teamALogo} />
              {series.teamA ?? 'TBD'}
            </span>
            <span className="tabular text-lg">
              <span className={aLeads ? 'text-win' : 'text-ink-faint'}>{series.scoreA}</span>
              <span className="mx-1 text-ink-faint">–</span>
              <span className={bLeads ? 'text-win' : 'text-ink-faint'}>{series.scoreB}</span>
            </span>
            <span
              className={`flex items-center gap-1.5 text-sm ${bLeads ? 'font-semibold text-ink' : 'text-ink-soft'}`}
            >
              <TeamBadge name={series.teamB} logo={series.teamBLogo} />
              {series.teamB ?? 'TBD'}
            </span>
          </div>

          <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-ink-faint">
            <span className="tabular shrink-0">{formatTime(series.startTime)}</span>
            {/* Only shown where leagues actually differ. Repeating the tournament name on every card
                inside its own hub is noise. */}
            {showLeague && series.leagueName ? (
              <span className="truncate">{series.leagueName}</span>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs text-ink-faint">
            {series.seriesLabel}
          </span>
          {series.isLive ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-live">
              <span className="size-1.5 animate-pulse rounded-full bg-live motion-reduce:animate-none" />
              LIVE
            </span>
          ) : !started ? (
            <span className="whitespace-nowrap text-xs text-ink-faint">
              {relativeTime(series.startTime)}
            </span>
          ) : null}
        </div>
      </header>

      <Forecast series={series} />

      <ol className="divide-y divide-line">
        {/* Keyed on the game number, not the match id: a map nobody has opened a lobby for has no
            match id, and every unplayed map of a series would share the same null key. */}
        {series.maps.map((map) => (
          <MapRow key={map.gameNumber} map={map} series={series} />
        ))}
      </ol>

      {series.maps.every((m) => m.preDraftTeamA === null && m.postDraftTeamA === null) ? (
        <p className="px-4 pb-3 pt-1 text-xs text-ink-faint">
          No prediction on record — these maps finished before the service was watching.
        </p>
      ) : null}

      {series.bet && series.bet.length > 0 ? (
        <Bets bets={series.bet} unpriced={series.unpricedMarkets ?? []} />
      ) : null}
    </article>
  )
}

/**
 * What the percentages on a series card mean, said once for a whole grid of them.
 *
 * It used to be the last line of every card — the same ninety characters, twenty-odd times on one
 * day's view, roughly two thousand characters of identical text a reader scrolls past to reach the
 * next match. A legend is a property of the column, not of the row.
 */
export function SeriesLegend() {
  return (
    <p className="mb-2 text-xs text-ink-faint">
      {/* The order here is the order in the rows, and it has been wrong once: the rows led with
          post-draft while a pending row led with pre-draft, so this sentence described half the
          column. Pre-draft leads everywhere now because it always exists; post-draft is in the
          brackets because it exists only once ten heroes are locked. */}
      Percentages are for the team named first, on every row:{' '}
      <Term id="pre-draft">pre-draft</Term>, then (<Term id="post-draft">post-draft</Term>) where
      the heroes were seen.
    </p>
  )
}

/**
 * One team's line in the phone scoreboard: badge, name, and its own score.
 *
 * The name truncates rather than wrapping, so the score stays on the same line as the team it
 * belongs to however long the name is — which is the entire reason this layout exists.
 */
function TeamRow({
  name,
  logo,
  score,
  leading,
}: {
  name: string | null
  logo: string | null | undefined
  score: number
  leading: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <TeamBadge name={name} logo={logo} />
      <span
        className={`min-w-0 flex-1 truncate text-sm ${
          leading ? 'font-semibold text-ink' : 'text-ink-soft'
        }`}
      >
        {name ?? 'TBD'}
      </span>
      <span className={`tabular shrink-0 text-base ${leading ? 'text-win' : 'text-ink-faint'}`}>
        {score}
      </span>
    </div>
  )
}

/**
 * One map of the series.
 *
 * **Two layouts, because five columns do not fit a phone.** The desktop row — game number, time,
 * result, both probabilities, verdict — needs about 385px before it will compress, and the column
 * a card sits in on an iPhone SE is 309px. Squeezing it leaves roughly twenty pixels for the result,
 * which is not a narrower version of the row so much as an unreadable one.
 *
 * So below `sm` the same facts are stacked into two lines: the identifying pair (which game, what
 * time) above, the outcome and what we said about it below. Nothing is dropped — a phone reader
 * gets both numbers, and the pre-draft one leads, because that is the one a bet could actually
 * have been placed at.
 */
function MapRow({ map, series }: { map: SeriesView['maps'][number]; series: SeriesView }) {
  /*
   * Four states, and `void` is the one that needs a word of its own.
   *
   * A Bo3 decided 2-0 never plays its third map. The row existed nowhere before — the list was built
   * from maps that had become matches, so a series simply stopped short — while the bookmaker block
   * on the same card listed Map 3, because it enumerates what the format allows. Saying "not played"
   * without saying why reads as missing data, and it also collides with the `teamAWon === null` case
   * two lines down, which really is missing data. The two must not read the same.
   *
   * This used to spell the reason out in the row: "not played — series decided". That is the right
   * fact and the wrong place for it. The explanation is identical on every void row, so on a page of
   * finished Bo3s it printed three, four, five times — furniture, read once and scrolled past for
   * ever after, paid for in vertical space each time. `prose-check` caught exactly that and said so:
   * make it a legend, a tap target, or delete it.
   *
   * So the row carries the word and the word carries its definition, which is what `Term` is for.
   * `void-bet` already existed and already said the better version of this sentence — including that
   * the stake comes back, which the row never mentioned and which is the part a reader actually
   * needs. See CLAUDE.md on why this is a Term rather than a `title` attribute.
   */
  const result =
    map.status === 'void' ? (
      <Term id="void-bet" className="text-void">
        Void
      </Term>
    ) : map.status === 'live' ? (
      <span className="text-ink-faint">in progress</span>
    ) : map.status === 'awaiting' ? (
      /*
       * Played, and we are waiting on the result — which is NOT the same as not started, and used
       * to render as it. OpenDota indexes hours behind during a tournament, so a map that finished
       * an hour ago routinely has no result here yet; on 2026-08-20 game one of BoomBoys vs TEAM
       * VISION sat reading "Not started" beside a live game two, which is a card contradicting
       * itself. The row says which of the two it is now.
       */
      <Term id="awaiting-result" className="text-ink-faint">
        Result pending
      </Term>
    ) : map.status === 'pending' ? (
      // "Not started", not "to play". The row sits in a column with "in progress" and "Void" above
      // and below it, and those describe a state rather than an intention.
      <span className="text-ink-faint">Not started</span>
    ) : map.teamAWon === null ? (
      <span className="text-ink-faint">not played</span>
    ) : (
      <span className="text-ink">{map.teamAWon ? series.teamA : series.teamB} won</span>
    )

  /*
   * EVERY number in this column is team A's. It used not to be, and that was the bug.
   *
   * The previous rule expressed a played map's figure for the WINNER of that map and an unplayed
   * one's for team A. Each row was defensible alone and the column was unreadable: Nigma Galaxy vs
   * OG showed 42% on game one (OG's) above 58% on game two (Nigma's), which reads as a swing and is
   * in fact the same forecast written from opposite ends. A reader scanning a column cannot see that
   * the referent moved.
   *
   * The general rule is older than this card: every cell in a column shares one meaning, which is
   * what makes a table legible at all. Odds boards get there the same way — the referent is fixed by
   * POSITION, home always in the same slot, never by which side happened to win.
   *
   * There is a statistical objection too. A probability reported for the winner is conditioned on
   * the outcome, so a column of them cannot be read as a forecast record: whether a figure sits
   * above or below evens then says only whether we were right, not what we claimed.
   *
   * The original complaint was real and is answered differently: the row said `TEAM VISION won
   * (49%)` where 49% was the other side's. The fix is to NAME the team the number is about, not to
   * move the number onto whoever won.
   */
  const played = map.teamAWon !== null

  const subject = series.teamA
  const post = map.postDraftTeamA
  const pre = map.preDraftTeamA

  /*
   * Colour by how far from a coin flip, not by right or wrong.
   *
   * Every finished map used to carry a green tick or a red cross. But a 52% call that lands is not a
   * success and a 52% call that misses is not a failure — the model said it did not know. Grading a
   * probability as a pick is the exact category error the Model page exists to correct, and the hub
   * was making it twenty times a screen on the more-visited page. Inside five points of even, this
   * renders as nothing happening, which is what happened.
   */
  const tone = (p: number | null) =>
    p === null || Math.abs(p - 0.5) < 0.05 ? 'text-ink-faint' : p > 0.5 ? 'text-win' : 'text-loss'

  /*
   * A map with no lobby yet gets one number, and it is not dressed as a record.
   *
   * Nothing here was ever published as a prediction: no match exists, so no Prediction row does
   * either. It is computed on read, like the favourites ranking, and it says so. The figure is the
   * pre-draft one, which is the same figure a played row leads with — see below.
   */
  const estimate = map.status === 'pending' ? map.preDraftTeamA : null

  const probabilities = estimate !== null ? (
    <>
      <Detail
        className={tone(estimate)}
        label={`What we would give ${series.teamA ?? 'team A'} now`}
        heading={<span className="inline-block w-9 text-right">{(estimate * 100).toFixed(0)}%</span>}
      >
        Our chance for <strong className="text-ink">{series.teamA}</strong> to win this map, from
        team strength alone — nobody has drafted it yet. It is an estimate made now rather than a
        prediction we published at the time, and it will move once the heroes are locked.
      </Detail>
      {/* The bracket keeps its width so the column of pre-draft figures above and below stays a
          column. "n/a" rather than a blank: nobody has drafted this map, which is a different fact
          from a draft we failed to see. */}
      <span className="ml-1.5 inline-block w-11 text-right text-ink-faint">(n/a)</span>
    </>
  ) : (
    <>
      {/*
        Pre-draft leads and post-draft rides in the brackets, on every row.
       *
       * It used to be the other way round on a played map and this way round on a pending one, so
       * one column meant two different things depending on how far down the card you had scrolled.
       * The rule that resolves it is a fact about the data rather than a preference: a pre-draft
       * number exists for every map we have a fixture for, and a post-draft one exists only once
       * ten heroes are locked. The figure that is always there is the one that can hold the
       * column; the optional one is what brackets are for.
       *
       * It is also the number that matters to a reader: the market closes when the game starts, so
       * pre-draft is the only price anybody could actually have taken.
      */}
      {pre !== null ? (
        <Detail
          className={tone(pre)}
          label={`What we gave ${subject ?? 'this team'} before the draft`}
          heading={
            <span className="inline-block w-9 text-right">{(pre * 100).toFixed(0)}%</span>
          }
        >
          Our chance for <strong className="text-ink">{subject}</strong> to win this map, from team
          strength alone, before the heroes were picked. This is the number a bet could have been
          placed at, because the market closes when the game starts.
          {played ? ' They won it.' : ''}
        </Detail>
      ) : null}
      {post !== null ? (
        <Detail
          className="ml-1.5 text-ink-faint"
          label={`What we gave ${subject ?? 'this team'} after the draft`}
          heading={
            <span className="inline-block w-11 text-right">({(post * 100).toFixed(0)}%)</span>
          }
        >
          The same chance for <strong className="text-ink">{subject}</strong> estimated again once
          all ten heroes were locked. Nobody could bet into it — the market had closed — but it is
          the better forecast, because it knows the draft.
        </Detail>
      ) : null}
      {/* A played map with no post-draft figure: the heroes were never seen, so there is nothing
          to put here. Stated rather than left blank, because a blank in a column of brackets reads
          as a rendering fault. */}
      {post === null && pre !== null ? (
        <Detail
          className="ml-1.5 text-ink-faint"
          label="No post-draft number for this map"
          heading={<span className="inline-block w-11 text-right">(n/a)</span>}
        >
          We never saw all ten heroes for this map, so there is no post-draft estimate — rather than
          the pre-draft one repeated under a label it has not earned.
        </Detail>
      ) : null}
      {post === null && pre === null ? <span className="text-ink-faint">—</span> : null}
    </>
  )

  return (
    <li className="px-3 py-2 text-xs sm:px-4">
      {/* Line one on a phone; the whole row from `sm` up. */}
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="shrink-0 whitespace-nowrap text-ink-faint sm:w-12">
          Game {map.gameNumber}
        </span>
        {/* No side here. Which team drew radiant is GAME information, of a piece with the
            heroes each of them picked, and this card is about the series and the prices on it. A
            single fact from that set, printed in a column and truncated to "Aurora Gaming rad…",
            is the worst of both: too little to be useful and enough to make the row look busy.
            If it comes back it comes back whole, in a panel of its own — see todo.md.

            Removing it also fixed the alignment underneath. The cell was `max-w-28` with no width
            of its own, so despite the comment that used to sit here claiming the cells "keep their
            width", an empty one collapsed to nothing — which is why a pending row's result began
            further left than a played row's. Every remaining cell has a fixed width or is the
            flexible one, so the columns line up in all four states. */}
        <span className="tabular shrink-0 text-ink-faint sm:w-12">
          {map.startTime === null ? '' : formatTime(map.startTime)}
        </span>

        {/* The result rides in the row only where there is room for it. */}
        <span className="hidden min-w-0 flex-1 truncate sm:inline">{result}</span>

        {/* ml-auto pins these right on a phone, where there is no flex-1 spacer before them. */}
        <span className="tabular ml-auto shrink-0 whitespace-nowrap text-right text-ink-soft sm:ml-0 sm:w-24">
          {probabilities}
        </span>
      </div>

      {/* Line two on a phone only. truncate needs min-w-0 above it, which the block provides. */}
      <div className="mt-0.5 min-w-0 truncate sm:hidden">{result}</div>
    </li>
  )
}

/** The forecast band, which asks a different question depending on whether the series is over. */
function Forecast({ series }: { series: SeriesView }) {
  // Decided: report what we said beforehand and whether it held up. There is no forecast left.
  if (series.decided) {
    if (series.preSeriesProbabilityTeamA === null) {
      return null
    }

    /*
     * What we gave the team that actually won, rather than a verdict on whether we picked them.
     *
     * This band used to end in `called it` or `missed`, green or red, on every finished series —
     * including ones where the model said 52% and had, in substance, declined to have an opinion.
     * Grading a probability as though it were a pick is precisely the error the Model page argues
     * against, and it was being made here twenty times a screen.
     *
     * Stating the winner's number instead is strictly more informative: above 50 we favoured them,
     * below we did not, and how far from 50 says how strongly. Inside five points it is drawn as
     * nothing at all, because nothing is what the model claimed.
     */
    const winnerIsA = series.scoreA > series.scoreB
    const winner = winnerIsA ? series.teamA : series.teamB
    const chance = winnerIsA
      ? series.preSeriesProbabilityTeamA
      : 1 - series.preSeriesProbabilityTeamA
    const tone =
      Math.abs(chance - 0.5) < 0.05 ? 'text-ink-faint' : chance > 0.5 ? 'text-win' : 'text-loss'

    return (
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2 text-xs">
        <span className="text-ink-soft">
          Going in, we gave {winner}{' '}
          <span className={`tabular ${tone}`}>{(chance * 100).toFixed(0)}%</span> to win the series
        </span>
        <span className={tone}>
          {Math.abs(chance - 0.5) < 0.05
            ? 'no strong call'
            : chance > 0.5
              ? 'we favoured them'
              : 'against our call'}
        </span>
      </div>
    )
  }

  // Still open: a genuine forecast from the current score, so a bar earns its place.
  if (series.seriesProbabilityTeamA === null) {
    return null
  }

  const favouredA = series.seriesProbabilityTeamA >= 0.5
  const favourite = favouredA ? series.teamA : series.teamB
  const chance = favouredA ? series.seriesProbabilityTeamA : 1 - series.seriesProbabilityTeamA

  return (
    <div className="border-b border-line px-4 py-3">
      <div className="mb-1.5 flex items-baseline justify-between text-xs">
        <span className="text-ink-faint">To win the series</span>
        <span className="tabular text-ink">
          {(chance * 100).toFixed(0)}% {favourite}
        </span>
      </div>
      <div className="flex h-2 overflow-hidden rounded-full bg-surface-raised">
        <div className="bg-accent" style={{ width: `${chance * 100}%` }} />
      </div>
      {series.mapProbabilityTeamA !== null ? (
        <p className="mt-1.5 text-xs text-ink-faint">
          {/* Spelled out: a bare "47% per map" leaves the reader guessing whose 47% it is. */}
          {(
            (favouredA ? series.mapProbabilityTeamA : 1 - series.mapProbabilityTeamA) * 100
          ).toFixed(0)}
          % for {favourite} in the next map — a series magnifies the edge
        </p>
      ) : null}
    </div>
  )
}

/**
 * What the market offered, and what we made of it.
 *
 * This lived only on the upcoming-fixture card, which disappears at kick-off — so the betting
 * advice was visible exactly until the moment its outcome could be checked, and never afterwards.
 * A reader could not tell whether a finished match had carried a bet at all.
 *
 * Odds are shown whether or not the edge cleared the bar. "We looked and there was nothing here" is
 * a real answer, and hiding the near-misses would make the record look more selective than it is.
 */
/**
 * The edge, and the subtraction behind it.
 *
 * The upcoming cards work this through in a panel; a settled row printed the answer on its own, so
 * the one number a reader is most likely to challenge was the one number with no derivation behind
 * it. Both halves of the sum are already on the wire — the implied price is ours minus the edge —
 * so this needs nothing the card did not have.
 */
function EdgeFigure({ bet }: { bet: SeriesBet }) {
  const ours = bet.ourProbability * 100
  const market = (bet.ourProbability - bet.edge) * 100
  const points = bet.edge * 100

  return (
    <Detail
      className={`tabular w-20 shrink-0 text-right ${bet.edge > 0 ? 'text-win' : 'text-ink-faint'}`}
      label="How this edge was worked out"
      heading={
        <span>
          {points > 0 ? '+' : ''}
          {points.toFixed(1)} pts
        </span>
      }
    >
      We made <strong className="text-ink">{bet.side}</strong> {ours.toFixed(1)}% for this market.
      At {bet.odds.toFixed(2)} the price implies {market.toFixed(1)}%, margin and all — which is
      what a stake here is paid at, so it is what the difference is measured against. That
      difference is {points.toFixed(1)} points, {bet.recommended ? 'over' : 'under'} the bar we
      need before calling anything worth backing.
    </Detail>
  )
}

function Bets({ bets, unpriced }: { bets: SeriesBet[]; unpriced: string[] }) {
  // The same query key the model page uses, so this is a cache read rather than another request.
  const { data: model } = useQuery({ queryKey: ['model'], queryFn: api.model })
  const modelVersion = model?.modelVersion ?? null

  return (
    <div className="border-t border-line px-4 py-3">
      <h4 className="flex flex-wrap items-baseline gap-x-2 text-xs font-medium text-ink-soft">
        {/*
          The heading names the model, because every verdict below depends on which one made it.
          It says "suggested bets" while the list also carries the markets we looked at and
          DECLINED, badged "no edge" — kept deliberately, because hiding the near-misses would make
          the record read as more selective than it is, and each row states its own verdict inline.
        */}
        <span>
          Suggested bets according to{' '}
          <a href="#/model" className="text-ink underline decoration-dotted underline-offset-2">
            model{modelVersion === null ? '' : ` (${modelVersion})`}
          </a>
          :
        </span>
        {bets.some((b) => b.pricedBeforeDraft) ? (
          <Detail
            label="When these prices were struck"
            className="font-normal text-ink-faint"
            heading={<span>pre-draft prices</span>}
          >
            Struck before the draft was visible, and the book did not reprice once the heroes were
            locked. So these odds answer a question asked with less information than our post-draft
            number had.
          </Detail>
        ) : null}
      </h4>
      <ul className="mt-2 space-y-1.5">
        {bets.map((bet) => (
          <li key={`${bet.market}-${bet.bookmaker}`} className="flex flex-wrap items-baseline gap-x-2 text-xs">
            {/* Fixed width, so Series and Map 1 start their prices at the same x — and so the
                edge column after them is a column rather than a ragged edge. */}
            <span className="w-12 shrink-0 text-ink-faint">{marketLabel(bet.market)}</span>
            <span className="min-w-0 flex-1 truncate text-ink">
              {bet.side} <span className="tabular">@ {bet.odds.toFixed(2)}</span>
            </span>
            <EdgeFigure bet={bet} />
            {bet.voided ? (
              <Detail
                label="Void bet — why?"
                heading={
                  <span className="rounded bg-void-bg px-1.5 py-0.5 text-xs uppercase tracking-wide text-void">
                    void
                  </span>
                }
              >
                The series ended before this map was played, so the stake comes back. Neither a win
                nor a loss — scoring it as either would invent a result.
              </Detail>
            ) : bet.won === null ? (
              bet.recommended ? (
                <span className="rounded bg-accent-bg px-1.5 py-0.5 text-xs uppercase tracking-wide text-accent">
                  advised
                </span>
              ) : (
                <span className="text-xs uppercase tracking-wide text-ink-faint">no edge</span>
              )
            ) : (
              <span
                className={`rounded px-1.5 py-0.5 text-xs uppercase tracking-wide ${
                  bet.won ? 'bg-win-bg text-win' : 'bg-loss-bg text-loss'
                }`}
              >
                {bet.won ? 'won' : 'lost'}
              </span>
            )}
          </li>
        ))}
        {/*
          Markets our book never put a usable price on, named rather than left out.
          
          Pinnacle opens a Bo3's third map only once the series needs one, so both TI series on
          2026-08-20 listed Series, Map 1 and Map 2 and stopped — while eighteen other books had
          priced Map 3. An absent row cannot be told apart from a market nobody priced, and those
          are different facts. Quoting a different book for this row instead is the thing not to
          do: the column would then mix two books with nothing saying which is which.
        */}
        {unpriced.map((market) => (
          <li key={`unpriced-${market}`} className="flex flex-wrap items-baseline gap-x-2 text-xs">
            <span className="w-12 shrink-0 text-ink-faint">{marketLabel(market)}</span>
            <Term id="not-priced" className="min-w-0 flex-1 truncate text-ink-faint">
              not priced by this book
            </Term>
          </li>
        ))}
      </ul>
    </div>
  )
}
